import {
    useCreateLogoUploadUrlMutation,
    useUpdateLogoMutation,
} from "@api/entity.api";
import {
    MAX_ORGANIZATION_LOGO_FILE_SIZE_BYTES,
    MAX_ORGANIZATION_LOGO_FILE_SIZE_MB,
} from "@common/constants/organization";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useEntity } from "@components/entity/useEntity";
import axios from "axios";
import { FC, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { entityLogoUploadContext } from "./entityLogoUpload.context";

const LOGO_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
];

const ERROR_CODES: Record<string, string> = {
    "file-too-large": `Max allowed size is ${MAX_ORGANIZATION_LOGO_FILE_SIZE_MB}MB`,
    "file-invalid-type": "Only PNG, JPEG, GIF, and WEBP files are allowed",
};

export const EntityLogoUploadProvider: FC<PropsWithChildren> = ({
    children,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const entity = useEntity();
    const createLogoUploadUrlMutation = useCreateLogoUploadUrlMutation(
        entity.id,
    );
    const updateLogoMutation = useUpdateLogoMutation(entity);
    const { error } = useToaster();

    const handleRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            fileRejections.forEach((rejection) => {
                error(
                    ERROR_CODES[rejection.errors[0].code]
                        ? `${rejection.file.name} - ${
                              ERROR_CODES[rejection.errors[0].code]
                          }`
                        : "Unknown error when uploading file",
                );
            });
        },
        [error],
    );

    const handleAccepted = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                setIsUploading(true);
                const file = acceptedFiles[0];

                const { url, path } =
                    await createLogoUploadUrlMutation.mutateAsync(file.name);

                await axios.put(url, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });

                await updateLogoMutation.mutateAsync(path);
            } catch {
                error("Failed to upload logo");
            } finally {
                setIsUploading(false);
            }
        },
        [createLogoUploadUrlMutation, updateLogoMutation, error],
    );

    const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
        onDropRejected: handleRejected,
        onDropAccepted: handleAccepted,
        accept: LOGO_IMAGE_TYPES,
        maxSize: MAX_ORGANIZATION_LOGO_FILE_SIZE_BYTES,
        multiple: false,
        noClick: true,
    });

    const value = useMemo(
        () => ({
            isDragActive: isDragAccept,
            inputProps: getInputProps(),
            rootProps: getRootProps(),
            isUploading,
            open,
        }),
        [isDragAccept, getInputProps, getRootProps, isUploading, open],
    );

    return (
        <entityLogoUploadContext.Provider value={value}>
            {children}
        </entityLogoUploadContext.Provider>
    );
};
